import { z } from 'zod';
import { tenantCodeSchema } from './common';
import { expoPushTokenSchema } from './devices';

export const PUSH_LIMITS = { maxMessagesPerRequest: 100, titleMax: 80, bodyMax: 180 } as const;

/** Push payloads carry no personal data: only tenantCode, type and refId (brief §10). */
export const pushDataSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    type: z.string().regex(/^[a-z][a-z0-9_.-]{0,39}$/),
    refId: z.string().max(64).nullable(),
  })
  .strict();
export type PushData = z.infer<typeof pushDataSchema>;

export const pushMessageSchema = z.object({
  to: expoPushTokenSchema,
  title: z.string().min(1).max(PUSH_LIMITS.titleMax),
  body: z.string().min(1).max(PUSH_LIMITS.bodyMax),
  data: pushDataSchema,
});
export type PushMessage = z.infer<typeof pushMessageSchema>;

/** POST central /v1/push/send — Tailscale only, per-tenant API key (brief §5.3). */
export const pushSendRequestSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    messages: z.array(pushMessageSchema).min(1).max(PUSH_LIMITS.maxMessagesPerRequest),
  })
  .refine((req) => req.messages.every((m) => m.data.tenantCode === req.tenantCode), {
    message: 'Every message must carry the sending tenant code',
    path: ['messages'],
  });
export type PushSendRequest = z.infer<typeof pushSendRequestSchema>;

/**
 * Expo ticket/receipt error codes (`details.error`) the relay passes through. Anything else is
 * reported as 'Unknown'. Kept as plain strings on the wire so new codes never break old clients.
 */
export const PUSH_ERROR_CODES = [
  'DeviceNotRegistered',
  'InvalidCredentials',
  'MessageTooBig',
  'MessageRateExceeded',
  'MismatchSenderId',
  'Unknown',
] as const;
export type PushErrorCode = (typeof PUSH_ERROR_CODES)[number];

/** Maps any Expo error string to a known code (never echoes free text, which can hold tokens). */
export function toPushErrorCode(raw: string | undefined | null): PushErrorCode {
  return (PUSH_ERROR_CODES as readonly string[]).includes(raw ?? '')
    ? (raw as PushErrorCode)
    : 'Unknown';
}

export const pushSendResponseSchema = z.object({
  accepted: z.number().int().min(0),
  tickets: z.array(
    z.object({
      to: expoPushTokenSchema,
      status: z.enum(['ok', 'error']),
      /** On error: the Expo error code (see PUSH_ERROR_CODES), never Expo's free text. */
      message: z.string().optional(),
      /** On error: same code, explicit field (additive; prefer this over `message`). */
      error: z.string().optional(),
    }),
  ),
});
export type PushSendResponse = z.infer<typeof pushSendResponseSchema>;

/** Central → tenant: tokens Expo reported as invalid, so the school deletes them. */
export const invalidTokensReportSchema = z.object({ tokens: z.array(expoPushTokenSchema) });
export type InvalidTokensReport = z.infer<typeof invalidTokensReportSchema>;

/** The app drops any push whose tenantCode doesn't match the current tenant. */
export function shouldDisplayPush(data: unknown, currentTenantCode: string | null): boolean {
  const parsed = pushDataSchema.safeParse(data);
  return (
    parsed.success && currentTenantCode !== null && parsed.data.tenantCode === currentTenantCode
  );
}

/** POST central /v1/push/receipts — school asks central to collect pending Expo receipts. */
export const pushReceiptsRequestSchema = z.object({ tenantCode: tenantCodeSchema });
export type PushReceiptsRequest = z.infer<typeof pushReceiptsRequestSchema>;
